/**
 * Switching which unit a contact popup writes to.
 *
 * A contact popup carries a dropdown of the other contact popups. Changing it
 * fetches that popup and replaces the dialog's content, so the visitor who
 * opened the wrong one is one choice away from the right one rather than
 * closing the dialog and hunting for another button.
 *
 * Only the content is replaced. The <dialog> stays the one the browser has
 * open, which keeps the top layer, the focus trap and Escape behaving.
 */

const SWITCHER = '.diakon-popup__switcher';
const CONTENT = '[data-diakon-popup-content]';
const BUSY = 'diakon-popup__content--busy';

/**
 * Re-runs Contact Form 7 over markup that arrived after page load.
 *
 * The plugin initialises its forms once on DOMContentLoaded, so a form fetched
 * later has no listeners: no AJAX submit, no validation, no response area.
 *
 * @param {HTMLElement} root Newly inserted markup.
 */
function initContactForms(root) {
	if (typeof window.wpcf7?.init !== 'function') {
		return;
	}

	root.querySelectorAll('form.wpcf7-form').forEach((form) => {
		window.wpcf7.init(form);
		form.closest('.wpcf7')?.classList.replace('no-js', 'js');
	});

	renderTurnstile(root);
}

/**
 * Draws the Turnstile widget of a form that arrived after page load.
 *
 * Turnstile draws the widgets that are on the page when its script loads, and
 * no others. A form fetched later would have none, send no token, and CF7
 * would turn every message from it away as spam. The site key and the name of
 * the field the token goes in are on the widget, as CF7 rendered it.
 *
 * @param {HTMLElement} root Newly inserted markup.
 */
function renderTurnstile(root) {
	if (typeof window.turnstile?.render !== 'function') {
		return;
	}

	root.querySelectorAll('.cf-turnstile').forEach((widget) => {
		if (widget.querySelector('iframe') || !widget.dataset.sitekey) {
			return;
		}

		window.turnstile.render(widget, {
			sitekey: widget.dataset.sitekey,
			'response-field-name':
				widget.dataset.responseFieldName || 'cf-turnstile-response',
		});
	});
}

/**
 * Puts focus back where the replaced switcher was, without reopening it.
 *
 * The control the visitor was using has just been thrown away with the markup
 * around it, so focus would otherwise fall out of the dialog. It goes to the
 * new switcher's wrapper rather than the switcher: focus() on a <select> opens
 * the native picker on iOS, so choosing a subject popped the dropdown open
 * again the moment the new content arrived.
 *
 * The wrapper is not focusable by itself -- tabindex="-1" makes it reachable
 * from script only, so it never joins the tab order. A Tab from there lands on
 * the switcher, which is where the visitor was.
 *
 * @param {HTMLSelectElement} switcher The switcher that just arrived.
 */
function restoreFocus(switcher) {
	const anchor = switcher.closest('.wpcf7-form-control-wrap') ?? switcher;

	if (anchor === switcher) {
		return;
	}

	anchor.tabIndex = -1;
	anchor.focus({ preventScroll: true });
}

/**
 * Loads a popup's content into the dialog already open.
 *
 * @param {HTMLElement} content The content element being replaced.
 * @param {string}      id      Popup to load.
 */
async function swap(content, id) {
	const { restUrl } = window.diakonPopupSwitcher ?? {};

	if (!restUrl) {
		return;
	}

	content.classList.add(BUSY);

	try {
		const response = await fetch(`${restUrl}popup/${id}`, {
			credentials: 'same-origin',
		});

		if (!response.ok) {
			return;
		}

		const popup = await response.json();

		content.innerHTML = popup.content;
		content.dataset.diakonPopupContent = String(popup.id);

		const dialog = content.closest('dialog');

		if (dialog && popup.title) {
			dialog.setAttribute('aria-label', popup.title);
		}

		initContactForms(content);

		// The replacement control is rendered by the server, which opens every
		// switcher on the blank option because it cannot know a choice was just
		// made here. It was, so it is put back -- otherwise choosing a subject
		// would clear the subject, and the form would fail validation on the
		// answer the visitor had already given.
		const switcher = content.querySelector(SWITCHER);

		if (switcher) {
			switcher.value = String(popup.id);
			restoreFocus(switcher);
		}
	} finally {
		content.classList.remove(BUSY);
	}
}

export function initPopupSwitcher() {
	// Delegated, because the control that replaces this one arrives with the
	// markup that replaced it.
	document.addEventListener('change', (event) => {
		const switcher = event.target.closest(SWITCHER);

		if (!switcher) {
			return;
		}

		const content = switcher.closest(CONTENT);

		if (!content) {
			return;
		}

		// The blank option is "no subject chosen", not "no popup": the dialog
		// goes back to the one it was opened as, so choosing it never leaves an
		// empty dialog or asks the server for popup ''.
		const target = switcher.value || content.dataset.diakonPopupDefault;

		if (!target || content.dataset.diakonPopupContent === target) {
			return;
		}

		swap(content, target);
	});
}
